import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import StarIcon from '@mui/icons-material/Star';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import React, { useCallback, useEffect, useState } from 'react';
import {
  clearImageFailure,
  ensureImageCached,
  getCachedBlobUrl,
  getCachedImageStatus,
  getCachedImageUrl,
  markImageFailed,
  markImageLoaded,
  retryImageFetch,
  subscribeToImageCache,
  ZoomMeta,
} from '../../hooks/useZoomPreview';
import { MasterCropData } from '../../types';

export interface CropThumbnailProps {
  crop: MasterCropData;
  sectionType: 'front' | 'non-front' | 'carton';
  size?: number;
  selectionVisualMode?: 'default' | 'master-label';
  isSelected?: boolean;
  isRepresentative?: boolean;
  isFocused?: boolean;
  isDisabled?: boolean;
  isPending?: boolean;
  showDeletionMark?: boolean;
  isLinkHighlighted?: boolean;
  highlightColor?: string;
  highlightGlow?: string;
  showTriangle?: boolean;
  isRecentlyMoved?: boolean;
  reloadVersion?: number;
  topLeftBadgeMode?: 'number' | 'warning' | null;
  topLeftBadgeLabel?: string;
  topLeftBadgeBackground?: string;
  topLeftBadgeBorder?: string;
  topLeftBadgeColor?: string;
  sourceBadgeLabel?: 'S' | 'F' | 'C' | 'A' | 'O';
  onClick?: (e: React.MouseEvent) => void;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}

function getReviewIssueBadgeLabel(
  reviewIssueType?: MasterCropData['reviewIssueType']
): 'D' | 'S' | 'O' | null {
  if (reviewIssueType === 'missing_design') return 'D';
  if (reviewIssueType === 'sop_unclear') return 'S';
  if (reviewIssueType === 'other') return 'O';
  return null;
}

const MASTER_LABEL_SELECTED_BORDER_COLOR = 'rgba(249,115,22,0.94)';
const MASTER_LABEL_SELECTED_RING = 'rgba(249,115,22,0.62)';
const MASTER_LABEL_MOVED_RING = 'rgba(63,185,80,0.88)';
const MASTER_LABEL_MOVED_GLOW = 'rgba(63,185,80,0.78)';
const MASTER_LABEL_MOVED_FILTER_BASE =
  'drop-shadow(0 0 6px rgba(63,185,80,0.38)) drop-shadow(0 0 14px rgba(63,185,80,0.28))';
const MASTER_LABEL_MOVED_ANIMATION_NAME = 'master-label-moved-glow';
const MASTER_LABEL_MOVED_KEYFRAMES_ID = 'master-label-moved-glow-keyframes';
const LINK_HIGHLIGHT_BORDER_WIDTH = '2.8px';
const BADGE_FILL_OPACITY_MULTIPLIER = 0.86;

function softenBadgeFill(color: string): string {
  const rgbaMatch = color.match(
    /^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([0-9]*\.?[0-9]+)\s*\)$/i
  );
  if (rgbaMatch) {
    const [, r, g, b, alpha] = rgbaMatch;
    const nextAlpha = Math.max(0, Math.min(1, Number(alpha) * BADGE_FILL_OPACITY_MULTIPLIER));
    return `rgba(${r}, ${g}, ${b}, ${nextAlpha.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')})`;
  }

  const rgbMatch = color.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch;
    return `rgba(${r}, ${g}, ${b}, ${BADGE_FILL_OPACITY_MULTIPLIER})`;
  }

  return color;
}

export const CropThumbnail: React.FC<CropThumbnailProps> = ({
  crop,
  sectionType,
  size = 46,
  selectionVisualMode = 'default',
  isSelected = false,
  isRepresentative = false,
  isFocused = false,
  isDisabled = false,
  isPending = false,
  showDeletionMark = false,
  isLinkHighlighted = false,
  highlightColor = 'var(--purple)',
  highlightGlow = '0 0 14px rgba(188,140,255,0.74), 0 0 30px rgba(188,140,255,0.34)',
  showTriangle = false,
  isRecentlyMoved = false,
  reloadVersion = 0,
  topLeftBadgeMode = null,
  topLeftBadgeLabel,
  topLeftBadgeBackground,
  topLeftBadgeBorder,
  topLeftBadgeColor,
  sourceBadgeLabel,
  onClick,
  onImageHover,
  onImageMove,
  onImageLeave,
}) => {
  const isInactive = crop.isInactive;
  const [imgError, setImgError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [imageSrc, setImageSrc] = useState(() => getCachedImageUrl(crop.imageUrl));

  const meta: ZoomMeta = {
    id: crop.id,
    storeId: crop.storeId,
    source: crop.source,
    forgeOriginSource: crop.forgeOriginSource,
    barcode: crop.barcode,
    normalizedBarcode: crop.normalizedBarcode,
    normalizedUpc: crop.normalizedUpc,
    classificationType: crop.classificationType,
    warningMessage:
      topLeftBadgeMode === 'warning' ? 'Barcode does not match known barcodes' : undefined,
    imageResolution: crop.imageResolution,
    createdBy: crop.createdBy,
    createdAt: crop.createdAt,
    updatedBy: crop.updatedBy,
    updatedAt: crop.updatedAt,
  };

  const handleImgError = useCallback(() => {
    if (getCachedImageStatus(crop.imageUrl) === 'pending') {
      setRetrying(true);
      return;
    }
    markImageFailed(crop.imageUrl);
    setImgError(true);
    setRetrying(false);
  }, [crop.imageUrl]);

  const handleImgLoad = useCallback(() => {
    markImageLoaded(crop.imageUrl);
    setImgError(false);
    setRetrying(false);
  }, [crop.imageUrl]);

  const handleRetry = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setRetrying(true);
      clearImageFailure(crop.imageUrl);
      setImgError(false);
      setImageSrc(getCachedBlobUrl(crop.imageUrl) ?? crop.imageUrl);
      retryImageFetch(crop.imageUrl, true);
    },
    [crop.imageUrl]
  );

  useEffect(() => {
    setImageSrc(getCachedBlobUrl(crop.imageUrl) ?? crop.imageUrl);
    setImgError(false);
    setRetrying(false);
    ensureImageCached(crop.imageUrl);
    setRetrying(getCachedImageStatus(crop.imageUrl) === 'pending');

    const unsubscribe = subscribeToImageCache(crop.imageUrl, () => {
      const blobUrl = getCachedBlobUrl(crop.imageUrl);
      if (blobUrl) {
        setImageSrc(blobUrl);
        setImgError(false);
        setRetrying(false);
        return;
      }

      if (getCachedImageStatus(crop.imageUrl) === 'pending') {
        setRetrying(true);
        return;
      }

      if (getCachedImageStatus(crop.imageUrl) === 'failed') {
        setRetrying(false);
        setImgError(true);
      }
    });

    return unsubscribe;
  }, [crop.imageUrl]);

  useEffect(() => {
    if (reloadVersion === 0) return;
    clearImageFailure(crop.imageUrl);
    retryImageFetch(crop.imageUrl, true);
    setImgError(false);
    setRetrying(true);
    setImageSrc(getCachedBlobUrl(crop.imageUrl) ?? crop.imageUrl);
  }, [reloadVersion, crop.imageUrl]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById(MASTER_LABEL_MOVED_KEYFRAMES_ID)) return;

    const style = document.createElement('style');
    style.id = MASTER_LABEL_MOVED_KEYFRAMES_ID;
    style.textContent = `
      @keyframes ${MASTER_LABEL_MOVED_ANIMATION_NAME} {
        0%, 100% {
          filter: drop-shadow(0 0 6px rgba(63,185,80,0.38))
            drop-shadow(0 0 14px rgba(63,185,80,0.28));
        }
        50% {
          filter: drop-shadow(0 0 12px rgba(63,185,80,0.62))
            drop-shadow(0 0 30px rgba(63,185,80,0.48));
        }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const triangleColor = crop.isBadImage
    ? 'var(--danger)'
    : sectionType === 'non-front' || sectionType === 'carton'
      ? 'var(--warning)'
      : crop.isMismatch
        ? 'var(--warning)'
        : 'var(--positive)';

  const triSize = size > 72 ? 16 : 14;
  const iconSize = Math.max(14, Math.round(size * 0.35));
  const isImageLoading = retrying && !imgError;
  const isFiveMp = crop.imageResolution?.toLowerCase() === '5mp';
  const reviewIssueBadgeLabel = showDeletionMark
    ? null
    : getReviewIssueBadgeLabel(crop.reviewIssueType);
  const reviewIssueBadgeSize = Math.max(14, Math.round(size * 0.24));
  const loadingBadgeSize = Math.max(14, Math.round(size * 0.28));
  const showFocusStyle = isLinkHighlighted || isFocused;
  const focusShadow = isLinkHighlighted
    ? highlightGlow
    : '0 0 12px rgba(188,140,255,0.6), 0 0 24px rgba(188,140,255,0.25)';
  const hoverTitle = `classification_type: ${crop.classificationType ?? 'unknown'}`;
  const reviewBadgeTopOffset = 3;
  const loadingTopOffset = reviewIssueBadgeLabel ? reviewIssueBadgeSize + 6 : 3;
  const fiveMpTopOffset = isImageLoading
    ? loadingTopOffset + loadingBadgeSize + 3
    : reviewIssueBadgeLabel
      ? reviewIssueBadgeSize + 6
      : isRepresentative
        ? 16
        : 3;
  const resolvedSourceBadgeLabel =
    sourceBadgeLabel ||
    (crop.source === 'scan'
      ? 'S'
      : crop.source === 'forge'
        ? 'F'
        : crop.source === 'control'
          ? 'C'
          : crop.source === 'at'
            ? 'A'
            : 'O');

  const sourceBadgeStyles =
    resolvedSourceBadgeLabel === 'S'
      ? {
          background: softenBadgeFill('rgba(63, 185, 80, 0.92)'),
          border: '1px solid rgba(164, 239, 176, 0.55)',
          color: '#f3fff5',
        }
      : resolvedSourceBadgeLabel === 'F'
        ? {
            background: softenBadgeFill('rgba(139, 92, 246, 0.92)'),
            border: '1px solid rgba(196, 181, 253, 0.6)',
            color: '#faf5ff',
          }
        : resolvedSourceBadgeLabel === 'A'
          ? {
              background: softenBadgeFill('rgba(88, 166, 255, 0.92)'),
              border: '1px solid rgba(145, 193, 255, 0.55)',
              color: '#f3f9ff',
            }
          : {
              background: softenBadgeFill('rgba(107, 114, 128, 0.92)'),
              border: '1px solid rgba(209, 213, 219, 0.45)',
              color: '#f9fafb',
            };
  const showOrientationBadge = crop.classificationType !== 'carton' && sectionType !== 'carton';
  const orientationBadgeLabel = crop.orientation === 'not-front' ? 'nF' : 'F';
  const orientationBadgeStyles =
    orientationBadgeLabel === 'F'
      ? {
          background: softenBadgeFill('rgba(63, 185, 80, 0.92)'),
          border: '1px solid rgba(164, 239, 176, 0.55)',
          color: '#f3fff5',
        }
      : {
          background: softenBadgeFill('rgba(107, 114, 128, 0.92)'),
          border: '1px solid rgba(209, 213, 219, 0.45)',
          color: '#f9fafb',
        };
  const isMasterLabelSelected = isSelected && selectionVisualMode === 'master-label';
  const selectedShadow = isMasterLabelSelected
    ? `0 0 0 3px ${MASTER_LABEL_SELECTED_RING}, 0 10px 24px rgba(15, 23, 42, 0.24)`
    : 'var(--crop-selected-shadow)';
  const movedShadow =
    selectionVisualMode === 'master-label' && isRecentlyMoved
      ? `0 0 0 3px ${MASTER_LABEL_MOVED_RING}, 0 0 26px ${MASTER_LABEL_MOVED_GLOW}`
      : null;
  const resolvedBorderWidth = isSelected
    ? isMasterLabelSelected
      ? '1.4px'
      : 'var(--crop-selected-border-width)'
    : showFocusStyle
      ? LINK_HIGHLIGHT_BORDER_WIDTH
      : isInactive || isPending
        ? '1px'
        : '2px';
  const resolvedBorderStyle = isSelected
    ? 'solid'
    : showFocusStyle
      ? 'solid'
      : isInactive || isPending
        ? 'dashed'
        : 'solid';
  const resolvedBorderColor = isSelected
    ? isMasterLabelSelected
      ? MASTER_LABEL_SELECTED_BORDER_COLOR
      : 'var(--crop-selected-border)'
    : showFocusStyle
      ? isLinkHighlighted
        ? highlightColor
        : 'var(--purple)'
      : isInactive || isPending
        ? 'var(--text-tertiary)'
        : 'var(--border)';
  const resolvedBoxShadow = isSelected
    ? movedShadow
      ? `${selectedShadow}, ${movedShadow}`
      : selectedShadow
    : showFocusStyle
      ? focusShadow
      : movedShadow || 'none';
  const resolvedAnimation = isRecentlyMoved
    ? selectionVisualMode === 'master-label'
      ? `${MASTER_LABEL_MOVED_ANIMATION_NAME} 2.1s ease-in-out infinite`
      : 'cropLanded 1.8s ease-out forwards'
    : showFocusStyle
      ? 'pulse-glow 1.5s ease-in-out infinite'
      : undefined;
  const resolvedFilter =
    selectionVisualMode === 'master-label' && isRecentlyMoved
      ? MASTER_LABEL_MOVED_FILTER_BASE
      : undefined;
  const resolvedTransform = isSelected
    ? isMasterLabelSelected
      ? 'scale(1.06) translateY(-1px)'
      : undefined
    : undefined;
  const resolvedZIndex = isSelected ? (isMasterLabelSelected ? 3 : undefined) : undefined;

  return (
    <div
      data-crop-id={crop.id}
      title={hoverTitle}
      onClick={onClick}
      onMouseEnter={onImageHover ? (e) => onImageHover(e, crop.imageUrl, meta) : undefined}
      onMouseMove={onImageMove ? (e) => onImageMove(e, crop.imageUrl, meta) : undefined}
      onMouseLeave={onImageLeave}
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
        cursor: isDisabled ? 'default' : 'pointer',
        borderWidth: resolvedBorderWidth,
        borderStyle: resolvedBorderStyle,
        borderColor: resolvedBorderColor,
        boxShadow: resolvedBoxShadow,
        opacity: isDisabled ? 0.5 : 1,
        transition: 'all 120ms ease',
        flexShrink: 0,
        animation: resolvedAnimation,
        filter: resolvedFilter,
        transform: resolvedTransform,
        zIndex: resolvedZIndex,
        minWidth: 0,
      }}
    >
      {imgError ? (
        <div
          onClick={handleRetry}
          style={{
            width: '100%',
            height: '100%',
            background: '#1a1a1a',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            cursor: 'pointer',
          }}
          title="Click to retry"
        >
          <BrokenImageOutlinedIcon
            style={{
              fontSize: iconSize,
              color: retrying ? 'var(--accent)' : 'var(--text-tertiary)',
              opacity: retrying ? 1 : 0.5,
              animation: retrying ? 'pulse-glow 1s ease-in-out infinite' : undefined,
            }}
          />
        </div>
      ) : (
        <img
          src={imageSrc}
          alt={crop.id}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            background: '#000',
            display: 'block',
          }}
          draggable={false}
          onLoad={handleImgLoad}
          onError={handleImgError}
        />
      )}
      {showDeletionMark && (
        <>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(248, 81, 73, 0.12)',
              pointerEvents: 'none',
            }}
          />
          <div
            data-testid={`crop-pending-deletion-${crop.id}`}
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '-12%',
                width: '124%',
                height: Math.max(5, Math.round(size * 0.1)),
                background: 'rgba(248, 81, 73, 0.96)',
                borderRadius: 999,
                transform: 'translateY(-50%) rotate(45deg)',
                boxShadow: '0 0 12px rgba(248,81,73,0.45)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '-12%',
                width: '124%',
                height: Math.max(5, Math.round(size * 0.1)),
                background: 'rgba(248, 81, 73, 0.96)',
                borderRadius: 999,
                transform: 'translateY(-50%) rotate(-45deg)',
                boxShadow: '0 0 12px rgba(248,81,73,0.45)',
              }}
            />
          </div>
        </>
      )}
      {isImageLoading && (
        <div
          style={{
            position: 'absolute',
            top: loadingTopOffset,
            right: 3,
            width: loadingBadgeSize,
            height: loadingBadgeSize,
            borderRadius: 999,
            background: softenBadgeFill('rgba(15, 15, 15, 0.78)'),
            border: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
          title="Image loading"
        >
          <AutorenewOutlinedIcon
            style={{
              fontSize: Math.max(10, Math.round(size * 0.18)),
              color: 'var(--accent)',
              animation: 'forge-spin 0.9s linear infinite',
            }}
          />
        </div>
      )}
      {reviewIssueBadgeLabel && (
        <div
          data-testid={`crop-review-issue-${crop.id}`}
          aria-label={`Review issue ${reviewIssueBadgeLabel}`}
          style={{
            position: 'absolute',
            top: reviewBadgeTopOffset,
            right: 3,
            minWidth: reviewIssueBadgeSize,
            height: reviewIssueBadgeSize,
            padding: '0 4px',
            borderRadius: 999,
            background: softenBadgeFill('rgba(201, 45, 66, 0.96)'),
            border: '1px solid rgba(255, 162, 172, 0.58)',
            color: '#fff5f7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            fontFamily: 'var(--font-mono)',
            fontSize: Math.max(7, Math.round(size * 0.12)),
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: '0.02em',
          }}
          title={`Needs review: ${crop.reviewIssueType?.replace(/_/g, ' ')}`}
        >
          {reviewIssueBadgeLabel}
        </div>
      )}
      {isFiveMp && (
        <div
          style={{
            position: 'absolute',
            top: fiveMpTopOffset,
            right: 3,
            minWidth: Math.max(15, Math.round(size * 0.36)),
            height: Math.max(9, Math.round(size * 0.17)),
            padding: '0 3px',
            borderRadius: 999,
            background: softenBadgeFill('rgba(201, 45, 66, 0.96)'),
            border: '1px solid rgba(255, 162, 172, 0.58)',
            color: '#fff5f7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            fontFamily: 'var(--font-mono)',
            fontSize: Math.max(5, Math.round(size * 0.1)),
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: '0.03em',
          }}
          title="5MP crop"
        >
          5MP
        </div>
      )}
      {topLeftBadgeMode && (
        <div
          style={{
            position: 'absolute',
            left: 3,
            top: 3,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              minWidth: Math.max(10, Math.round(size * 0.19)),
              height: Math.max(10, Math.round(size * 0.19)),
              padding: '0 2px',
              borderRadius: 999,
              background:
                topLeftBadgeMode === 'number'
                  ? softenBadgeFill(topLeftBadgeBackground || 'rgba(88, 166, 255, 0.92)')
                  : softenBadgeFill('rgba(210, 153, 34, 0.96)'),
              border:
                topLeftBadgeMode === 'number'
                  ? topLeftBadgeBorder || '1px solid rgba(145, 193, 255, 0.55)'
                  : '1px solid rgba(255, 220, 120, 0.6)',
              color: topLeftBadgeMode === 'number' ? topLeftBadgeColor || '#f3f9ff' : '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: Math.max(7, Math.round(size * 0.11)),
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {topLeftBadgeMode === 'warning' ? (
              <WarningAmberOutlinedIcon
                style={{ fontSize: Math.max(7, Math.round(size * 0.13)) }}
              />
            ) : (
              topLeftBadgeLabel || '?'
            )}
          </div>
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          left: 3,
          bottom: 3,
          minWidth: Math.max(10, Math.round(size * 0.19)),
          height: Math.max(10, Math.round(size * 0.19)),
          padding: '0 2px',
          borderRadius: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          fontFamily: 'var(--font-mono)',
          fontSize: Math.max(7, Math.round(size * 0.11)),
          fontWeight: 700,
          lineHeight: 1,
          ...sourceBadgeStyles,
        }}
      >
        {resolvedSourceBadgeLabel}
      </div>
      <div
        style={{
          position: 'absolute',
          right: 3,
          bottom: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          pointerEvents: 'none',
        }}
      >
        {showOrientationBadge && (
          <div
            style={{
              minWidth:
                orientationBadgeLabel === 'nF'
                  ? Math.max(15, Math.round(size * 0.28))
                  : Math.max(10, Math.round(size * 0.19)),
              height: Math.max(10, Math.round(size * 0.19)),
              padding: '0 3px',
              borderRadius: 999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: Math.max(7, Math.round(size * 0.11)),
              fontWeight: 700,
              lineHeight: 1,
              ...orientationBadgeStyles,
            }}
          >
            {orientationBadgeLabel}
          </div>
        )}
      </div>
      {/* Crop type triangle (demo only) */}
      {showTriangle && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            borderTop: `${triSize}px solid ${triangleColor}`,
            borderRight: `${triSize}px solid transparent`,
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Representative star */}
      {isRepresentative && (
        <StarIcon
          data-testid={`crop-representative-star-${crop.id}`}
          style={{
            position: 'absolute',
            top: 2,
            right: reviewIssueBadgeLabel ? reviewIssueBadgeSize + 5 : 2,
            fontSize: 12,
            color: '#E8976C',
            filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))',
          }}
        />
      )}
      {isSelected && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(232,151,108,0.15)',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};
